import { computed, ref } from "vue";

const CONSENT_BASIC_COOKIE = "CONSENT_BASIC";
const CONSENT_THIRD_COOKIE = "CONSENT_THIRD";
const CONSENT_DAYS = 365;

const initialized = ref(false);
const mainDialogOpen = ref(false);
const settingsDialogOpen = ref(false);
const analyticsEnabled = ref(false);

function canUseWindow(): boolean {
  return typeof window !== "undefined";
}

function ensureConsentBootstrap(): void {
  if (!canUseWindow()) {
    return;
  }

  window.gaPropertyId = window.gaPropertyId ?? "";
  window.dataLayer = window.dataLayer ?? [];

  if (typeof window.gtag !== "function") {
    window.gtag = (...args: unknown[]) => {
      window.dataLayer?.push(args);
    };
  }

  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });
}

function getCookie(name: string): string {
  if (!canUseWindow()) {
    return "";
  }

  const prefix = `${name}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookies = decodedCookie.split(";");

  for (const item of cookies) {
    const cookie = item.trim();
    if (cookie.startsWith(prefix)) {
      return cookie.substring(prefix.length);
    }
  }

  return "";
}

function setCookie(name: string, value: string, days: number): void {
  if (!canUseWindow()) {
    return;
  }

  const expiresDate = new Date();
  expiresDate.setTime(expiresDate.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expiresDate.toUTCString()};path=/;SameSite=Lax`;
}

function deleteCookie(name: string, path: string, domain?: string): void {
  let cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
  if (domain) {
    cookie += ` domain=${domain};`;
  }
  document.cookie = cookie;
}

function deleteAllGACookies(): void {
  if (!canUseWindow()) {
    return;
  }

  const domainParts = window.location.hostname.split(".");
  const mainDomain =
    domainParts.length > 1 ? `.${domainParts.slice(-2).join(".")}` : "";
  const currentDomain = window.location.hostname;
  const allCookies = document.cookie.split(";");

  for (const cookie of allCookies) {
    const cookieName = cookie.trim().split("=")[0];
    if (
      cookieName.startsWith("_ga") ||
      cookieName.startsWith("_gid") ||
      cookieName.startsWith("_gat") ||
      cookieName.startsWith("_gac")
    ) {
      deleteCookie(cookieName, "/", mainDomain || undefined);
      deleteCookie(cookieName, "/", currentDomain);
      deleteCookie(cookieName, "/");
      deleteCookie(cookieName, "");
    }
  }
}

function loadGoogleAnalytics(): void {
  if (!canUseWindow()) {
    return;
  }

  const gaPropertyId = window.gaPropertyId?.trim();
  if (!gaPropertyId) {
    return;
  }

  if (document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaPropertyId}`;
  script.onload = () => {
    if (typeof window.gtag === "function") {
      window.gtag("js", new Date());
      window.gtag("config", gaPropertyId);
    }
  };
  document.head.appendChild(script);
}

function applyAnalyticsConsent(enabled: boolean): void {
  ensureConsentBootstrap();

  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: enabled ? "granted" : "denied",
      ad_storage: enabled ? "granted" : "denied",
      ad_user_data: enabled ? "granted" : "denied",
      ad_personalization: enabled ? "granted" : "denied",
    });
  }

  if (enabled) {
    loadGoogleAnalytics();
    return;
  }

  deleteAllGACookies();
}

function syncDecisionFromStorage(): boolean {
  if (!canUseWindow()) {
    return false;
  }

  const consentBasic = getCookie(CONSENT_BASIC_COOKIE);
  const consentThird = getCookie(CONSENT_THIRD_COOKIE);
  const prefsStorage = localStorage.getItem("prefs");
  const analyticsStorage = localStorage.getItem("analytics");

  if (consentBasic !== "" && consentThird !== "") {
    if (prefsStorage !== consentBasic || analyticsStorage !== consentThird) {
      localStorage.setItem("prefs", consentBasic);
      localStorage.setItem("analytics", consentThird);
    }
    analyticsEnabled.value = consentThird === "true";
    return true;
  }

  if (prefsStorage !== null && analyticsStorage !== null) {
    setCookie(CONSENT_BASIC_COOKIE, prefsStorage, CONSENT_DAYS);
    setCookie(CONSENT_THIRD_COOKIE, analyticsStorage, CONSENT_DAYS);
    analyticsEnabled.value = analyticsStorage === "true";
    return true;
  }

  analyticsEnabled.value = false;
  return false;
}

function persistConsent(allowAnalytics: boolean): void {
  if (!canUseWindow()) {
    return;
  }

  localStorage.setItem("prefs", "true");
  localStorage.setItem("analytics", allowAnalytics ? "true" : "false");
  setCookie(CONSENT_BASIC_COOKIE, "true", CONSENT_DAYS);
  setCookie(CONSENT_THIRD_COOKIE, allowAnalytics ? "true" : "false", CONSENT_DAYS);
  analyticsEnabled.value = allowAnalytics;
  applyAnalyticsConsent(allowAnalytics);
  mainDialogOpen.value = false;
  settingsDialogOpen.value = false;
}

function initializeCookieConsent(): void {
  ensureConsentBootstrap();

  const hasDecision = syncDecisionFromStorage();
  if (hasDecision) {
    applyAnalyticsConsent(analyticsEnabled.value);
    mainDialogOpen.value = false;
    settingsDialogOpen.value = false;
    return;
  }

  mainDialogOpen.value = true;
  settingsDialogOpen.value = false;
}

function installCookieConsentGlobals(): void {
  if (!canUseWindow()) {
    return;
  }

  window.cookies_hasAnalyticsConsent = () => analyticsEnabled.value;
  window.check_cookies = () => {
    initializeCookieConsent();
  };
}

export function hasAnalyticsConsent(): boolean {
  return analyticsEnabled.value;
}

export function useCookieConsent() {
  if (!initialized.value) {
    installCookieConsentGlobals();
    initializeCookieConsent();
    initialized.value = true;
  }

  const preferencesAlwaysEnabled = computed(() => true);

  function acceptAll(): void {
    persistConsent(true);
  }

  function rejectAnalytics(): void {
    persistConsent(false);
  }

  function openSettings(): void {
    mainDialogOpen.value = false;
    settingsDialogOpen.value = true;
  }

  function closeSettings(): void {
    settingsDialogOpen.value = false;
    mainDialogOpen.value = true;
  }

  function confirmSettings(): void {
    persistConsent(analyticsEnabled.value);
  }

  return {
    analyticsEnabled,
    closeSettings,
    confirmSettings,
    mainDialogOpen,
    openSettings,
    preferencesAlwaysEnabled,
    rejectAnalytics,
    settingsDialogOpen,
    acceptAll,
  };
}
